/**
 * Which previews get a project thumbnail: frontends only. A backend's preview is the runner's API Portal page, or the
 * app's own JSON / plain-text answer ("Mongo consumer is running"); a database or a worker has nothing to show. A
 * screenshot of those is not a thumbnail anyone wants, and the server-side capture (POST /api/thumbnails/capture)
 * is not even attempted for them.
 */
import { detectServiceType } from './frameworkDetection';
import { isAppJsonBody } from './previewReadiness';

/** Words that name a data store (a service or resource called `postgres`, `mongo-db`, `orders-redis`…). */
const DATA_STORE_WORDS = new Set([
  'postgres', 'postgresql', 'pg', 'mongo', 'mongodb', 'mysql', 'mariadb', 'redis', 'valkey', 'neo4j', 'sqlite',
  'minio', 's3', 'kafka', 'rabbitmq', 'elasticsearch', 'opensearch', 'cassandra', 'db', 'database', 'crud',
]);

const words = (value) => String(value || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

/** A database (or similar data resource) by its name, image, type or framework. */
export function isDataStoreService(serviceName, service = null) {
  const lastSegment = String(serviceName || service?.name || '').split('/').filter(Boolean).pop() || '';
  const fields = [lastSegment, service?.dockerImage, service?.type, service?.kind, service?.framework, service?.stack];
  return fields.some((field) => words(field).some((word) => DATA_STORE_WORDS.has(word)));
}

/** The service says it is a backend: explicit tags, or a backend type / framework. */
function declaredBackend(service) {
  if (!service) { return false; }
  const tags = Array.isArray(service.tags) ? service.tags : [];
  if (tags.some((tag) => ['backend', 'be', 'crud', 'crud-service', 'worker'].includes(String(tag).toLowerCase()))) { return true; }
  const kind = words(`${service.type || ''} ${service.kind || ''}`);
  return kind.some((word) => ['backend', 'api', 'worker', 'job', 'crud'].includes(word));
}

/**
 * Whether a loaded preview document is a frontend's page, worth a thumbnail.
 * @param {{ contentType?: string, title?: string, bodyText?: string, bodyHTML?: string }|null} doc what the iframe
 *   shows (null when it cannot be read: a cross-origin preview)
 * @param {{ serviceName?: string, service?: object }} [about]
 */
export function shouldAutoCaptureThumbnail(doc, { serviceName = '', service = null } = {}) {
  if (isDataStoreService(serviceName, service) || declaredBackend(service)) { return false; }
  if (!doc) {
    // Nothing to look at: only a service that says it is a frontend.
    const tags = Array.isArray(service?.tags) ? service.tags.map((tag) => String(tag).toLowerCase()) : [];
    return tags.includes('frontend') || tags.includes('fe') || (Boolean(service) && detectServiceType(service).isFrontend);
  }
  const contentType = String(doc.contentType || 'text/html').toLowerCase();
  if (!contentType.includes('html')) { return false; } // JSON, plain text, an image…
  const title = String(doc.title || '');
  if (/^API Portal\b/.test(title)) { return false; } // the runner's page for a backend with no UI
  if (/— Flow Preview$/.test(title)) { return false; } // the runner's own starting / error / stopped pages
  const bodyText = String(doc.bodyText || '');
  if (isAppJsonBody(bodyText)) { return false; }
  // Text with no element at all (Express's `res.send('… is running')`): a backend's answer, not a page.
  const bodyHTML = String(doc.bodyHTML || '');
  if (!/<[a-z]/i.test(bodyHTML)) { return false; }
  return true;
}
